import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import useUsers from '../api/useUsers';
import { EFeatureFlag } from '../constants/enums';
import { pageFeatureFlagMap } from '../design-system/AcSidebar/constants';
import { useSidebar } from '../design-system/AcSidebar/context';
import { ESideBarChipType, ESideBarContext, SideBarItem } from '../design-system/AcSidebar/types';

interface FeatureFlagData {
  treatment: ESideBarChipType;
  config?: Record<string, string>;
}

const hasTreatment = (flagData?: FeatureFlagData) => {
  return Boolean(flagData && 
    typeof flagData === 'object' && 
    'treatment' in flagData);
};

const processFeatureFlagData = (
  data: any, 
  featureFlagKey: EFeatureFlag, 
  pageIdentifier?: string,
): { pageType: ESideBarChipType | null; isPageDisabled: boolean } => {
  const featureFlagData = data?.[featureFlagKey];
  
  // STATIC CASE: Global feature flag (treatment only)
  // Example: "dashboard_beta_features_test": { "treatment": "beta" }
  if (hasTreatment(featureFlagData)) {
    const treatment = featureFlagData.treatment;
    return {
      pageType: treatment,
      isPageDisabled: treatment === ESideBarChipType.LOCKED || treatment === ESideBarChipType.COMING_SOON
    };
  }
  
  // DYNAMIC CASE: Targeted feature flag (treatment + config)
  // Uses portal/project IDs in config to target specific entities
  // Example: "dashboard_beta_features_test": { "treatment": "beta", config: { "68ad51687fc4268f60fb6b90": "true" } }
  if (pageIdentifier) {

    const flagKeys = Object.keys(data || {});
    for (const flagKey of flagKeys) {
      const flagData = data?.[flagKey];
      
      if (hasTreatment(flagData)) {
        const config = flagData?.config;
        const isIdInConfig = config?.[pageIdentifier] === 'true';
        
        if (isIdInConfig) {
          const treatment = flagData.treatment;
          return {
            pageType: treatment,
            isPageDisabled: treatment === ESideBarChipType.LOCKED || treatment === ESideBarChipType.COMING_SOON
          };
        }
      }
    }
  }
  
  // DEFAULT CASE: No feature flag found, page is enabled by default
  return { pageType: null, isPageDisabled: false };
};

export const usePageTypeByFeatureFlag = (pageName: string) => {
  const { publisherId } = useParams();
  const { fetchFeatureFlags } = useUsers({ publisherId });
  const { data, isLoading, error } = fetchFeatureFlags;
  const [pageType, setPageType] = useState<ESideBarChipType | null>(null);
  
  const featureFlagKey = pageFeatureFlagMap[pageName] as EFeatureFlag;
  const isPageDisabled =
    pageType === ESideBarChipType.LOCKED ||
    pageType === ESideBarChipType.COMING_SOON;

  useEffect(() => {
    if (isLoading || error || !featureFlagKey) return;

    const result = processFeatureFlagData(data?.featureFlags, featureFlagKey);
    setPageType(result.pageType);
  }, [data, isLoading, error, featureFlagKey]);

  return { pageType, isPageDisabled };
};

export const usePageTypeByFeatureFlagAndContext = (page: SideBarItem) => {
  const { context } = useSidebar();
  const { publisherId } = useParams();
  
  const isCompanyContext = context === ESideBarContext.COMPANY;
  
  const { fetchFeatureFlags, getCompanyFeatureFlags } = useUsers({ 
    publisherId: isCompanyContext ? undefined : publisherId,
    enableFeatureFlags: true 
  });
  
  const [pageType, setPageType] = useState<ESideBarChipType | null>(null);
  const [isPageDisabled, setIsPageDisabled] = useState(false);
  
  const featureFlagKey = pageFeatureFlagMap[page?.id || page.title];

  useEffect(() => {
    const { data, isLoading, error } = isCompanyContext ? getCompanyFeatureFlags : fetchFeatureFlags;
    if (!data || isLoading || error) return;
    const featureFlagsData = isCompanyContext ? data : data?.featureFlags;
    const currentPageIdentifier = page?.id || page.title;
    const result = processFeatureFlagData(featureFlagsData, featureFlagKey, currentPageIdentifier);

    setPageType(result.pageType);
    setIsPageDisabled(result.isPageDisabled);

  }, [isCompanyContext, page?.id, page.title]);
  
  return { pageType, isPageDisabled };
};


